/**
 * Artikel aus Belegen → Material- und Maschinenliste der Lieferanten
 * (Migration 182, Backend: hetzner/webhook/lieferant_artikel.py).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@vimaana/ui";

/** Ein offener Artikel — je Lieferant und Artikel einmal, egal auf wie vielen Belegen. */
export type ArtikelOffen = {
  firma_id: string;
  firma: string;
  schluessel: string;
  bezeichnung: string;
  artikelnummer: string;
  art: string;
  einheit: string;
  preis: number | null;
  belege: number;
  /** Davon Lieferscheine (ohne Preis) — Migration 193. */
  lieferscheine?: number;
  /** Beinahe-Treffer im Katalog — nur zum Bestätigen, nie automatisch. */
  vorschlag: { id: string; name: string } | null;
};

export type BelegArtikelZeile = {
  nr: number;
  bezeichnung: string;
  artikelnummer: string;
  menge: number | null;
  einheit: string;
  einzelpreis_netto: number | null;
  art: string;
  stand: "offen" | "bekannt" | "ignoriert";
  material: string;
};

export type BelegArtikel = {
  gelesen: boolean;
  mit_lieferant: boolean;
  zeilen: BelegArtikelZeile[];
  offen: ArtikelOffen[];
};

export type ArtikelEntscheidung = {
  firma_id: string;
  schluessel: string;
  aktion: "aufnehmen" | "zuordnen" | "ignorieren";
  material_id?: string;
  name?: string;
  art?: string;
};

export function useBelegArtikel(belegId?: string) {
  return useQuery<BelegArtikel>({
    queryKey: ["artikel", "beleg", belegId],
    queryFn: () => apiGet<BelegArtikel>(`/api/kontor/belege/${encodeURIComponent(belegId || "")}/artikel`),
    enabled: !!belegId,
    staleTime: 30_000,
  });
}

export function useArtikelOffen(firmaId = "") {
  return useQuery<{ zeilen: ArtikelOffen[] }>({
    queryKey: ["artikel", "offen", firmaId],
    queryFn: () =>
      apiGet<{ zeilen: ArtikelOffen[] }>(
        `/api/kontor/artikel/offen${firmaId ? `?firma_id=${encodeURIComponent(firmaId)}` : ""}`,
      ),
    staleTime: 60_000,
  });
}

export function useArtikelEntscheiden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entscheidungen: ArtikelEntscheidung[]) =>
      apiPost<{ entschieden: number; fehler: string[] }>("/api/kontor/artikel/entscheiden", { entscheidungen }),
    onSuccess: () => {
      // Katalog, Lieferantenliste, Materialansicht und die Belege selbst.
      for (const k of [["artikel"], ["lieferant"], ["lieferanten"], ["material"]]) {
        qc.invalidateQueries({ queryKey: k });
      }
    },
  });
}
